/**
 * client/hud/bmfont.ts — BO2 bitmap fonts (pure data + measuring; drawing lives in gfx.ts).
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * BO2 ships only bitmap atlases (no TTF). game/public/assets/ui/fonts/<name>.json holds each
 * glyph's atlas rect, xoffset/yoffset relative to the BASELINE (negative = above) and xadvance,
 * at the font's native pixelHeight. All 720p fonts share fonts/gamefonts_pc_720.png
 * [hud.md "Fonts"]. LUI draws a text element inside a box whose height is the font size: we
 * place the baseline at top + size, exactly like tools/ui/render_hud_mock.py.
 *
 * LUI font table (ui/t6/codbase.lua:113-160, English):
 *   Default smallFont 25 | Condensed normalFont 30 | Italic italicFont 25 | SmallItalic
 *   smallItalicFont 20 | Big bigFont 48 | Morris extraBigFont 60 | ExtraSmall extraSmallFont 20 |
 *   Tiny extraSmallFont 16 | Dist distFont (36 px signed-distance atlas).
 */

export type LuiFont = 'Default' | 'Condensed' | 'Italic' | 'SmallItalic' | 'Big' | 'Morris' | 'ExtraSmall' | 'Tiny' | 'Dist';

export const LUI_FONTS: Record<LuiFont, { file: string; size: number }> = {
  Default: { file: 'smallFont', size: 25 },
  Condensed: { file: 'normalFont', size: 30 },
  Italic: { file: 'italicFont', size: 25 },
  SmallItalic: { file: 'smallItalicFont', size: 20 },
  Big: { file: 'bigFont', size: 48 },
  Morris: { file: 'extraBigFont', size: 60 },
  ExtraSmall: { file: 'extraSmallFont', size: 20 },
  Tiny: { file: 'extraSmallFont', size: 16 },
  // Dist is drawn at the requested size (25 px rows in the score feed, CoD.textSize.Default).
  Dist: { file: 'distFont', size: 25 },
};

export interface Glyph {
  x: number;
  y: number;
  w: number;
  h: number;
  xoffset: number;
  yoffset: number;
  xadvance: number;
}

export interface FontData {
  name: string;
  pixelHeight: number;
  atlas: string;
  /** The atlas is a signed-distance field (distFont). */
  sdf: boolean;
  glyphs: Map<string, Glyph>;
}

interface RawFont {
  name: string;
  pixelHeight: number;
  atlas: string;
  glyphs: { char: string; x: number; y: number; w: number; h: number; xoffset: number; yoffset: number; xadvance: number }[];
}

export function parseFont(raw: RawFont): FontData {
  const glyphs = new Map<string, Glyph>();
  for (const g of raw.glyphs) glyphs.set(g.char, { x: g.x, y: g.y, w: g.w, h: g.h, xoffset: g.xoffset, yoffset: g.yoffset, xadvance: g.xadvance });
  return { name: raw.name, pixelHeight: raw.pixelHeight, atlas: raw.atlas, sdf: raw.name === 'distFont', glyphs };
}

/** Glyph for a character, with the usual fallbacks (curly quotes, missing lowercase). */
export function glyphFor(font: FontData, ch: string): Glyph | undefined {
  return font.glyphs.get(ch) ?? font.glyphs.get(ch.toUpperCase()) ?? font.glyphs.get(FALLBACK[ch] ?? '') ?? font.glyphs.get('?');
}
const FALLBACK: Record<string, string> = { '‘': "'", '’': "'", '“': '"', '”': '"', '–': '-', '—': '-', ' ': ' ' };

/** Strip BO2 colour codes (^0..^9) and font-icon markup (^B...^) from a string. */
export function stripCodes(s: string): string {
  return s.replace(/\^B[^^]*\^/g, '').replace(/\^[0-9]/g, '');
}

/** Width of `text` in virtual px when drawn at `size` px. */
export function measureText(font: FontData, text: string, size: number): number {
  const s = size / font.pixelHeight;
  let w = 0;
  for (const ch of text) {
    const g = glyphFor(font, ch);
    if (g) w += g.xadvance;
  }
  return w * s;
}
